/**
 * OAuth Popup Authentication Utility
 * Opens X auth in a popup window instead of full redirect
 */

export interface AuthPopupOptions {
  redirect?: string;
  testflow?: boolean;
  onSuccess?: (token: string) => void;
  onError?: (error: string) => void;
}

let authPopup: Window | null = null;
let popupCheckInterval: NodeJS.Timeout | null = null;
let authCompleted = false; // Flag to prevent race condition

export function openAuthPopup(options: AuthPopupOptions = {}): void {
  const { redirect = 'auto', testflow = false, onSuccess, onError } = options;
  
  // Reset flag
  authCompleted = false;
  
  // Build auth URL
  const params = new URLSearchParams();
  params.set('redirect', redirect);
  params.set('popup', 'true'); // Tell backend this is a popup flow
  if (testflow) params.set('testflow', 'true');
  
  const authUrl = `/api/auth/x/login?${params.toString()}`;
  
  // Calculate popup position (centered)
  const width = 500;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  
  // Close any existing popup
  if (authPopup && !authPopup.closed) {
    authPopup.close();
  }
  
  // Open popup
  authPopup = window.open(
    authUrl,
    'auth_popup',
    `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
  );
  
  if (!authPopup) {
    // Popup was blocked, fall back to redirect
    console.warn('Popup blocked, falling back to redirect');
    window.location.href = authUrl.replace('popup=true', 'popup=false');
    return;
  }
  
  // Listen for messages from popup
  const handleMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    
    if (event.data?.type === 'AUTH_SUCCESS') {
      authCompleted = true; // Mark as completed FIRST
      window.removeEventListener('message', handleMessage);
      if (popupCheckInterval) clearInterval(popupCheckInterval);
      
      const { token, redirectUrl } = event.data;
      if (token) {
        localStorage.setItem('authToken', token);
        localStorage.setItem('jwt_token', token); // Also store as jwt_token for compatibility
        console.log('Token stored successfully');
      }
      
      if (onSuccess) {
        onSuccess(token);
      } else if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    } else if (event.data?.type === 'AUTH_ERROR') {
      window.removeEventListener('message', handleMessage);
      if (popupCheckInterval) clearInterval(popupCheckInterval);
      
      if (onError) {
        onError(event.data.error || 'Authentication failed');
      }
    }
  };
  
  window.addEventListener('message', handleMessage);
  
  // Check if popup was closed manually
  popupCheckInterval = setInterval(() => {
    if (authPopup?.closed) {
      if (popupCheckInterval) clearInterval(popupCheckInterval);
      window.removeEventListener('message', handleMessage);
      // Only notify error if auth didn't complete
      if (!authCompleted && onError) {
        onError('popup_closed');
      }
    }
  }, 500);
}

export function closeAuthPopup(): void {
  if (authPopup && !authPopup.closed) {
    authPopup.close();
  }
  if (popupCheckInterval) {
    clearInterval(popupCheckInterval);
  }
}
